import { useState } from "preact/hooks";
import { strings } from "../i18n/strings.es-AR.js";
import { downloadChatAttachment } from "../api/chat.js";
import { conversation } from "../state/store.js";
import { Icon } from "./icons.jsx";
import { track } from "../metrics/track.js";

/** Bytes → etiqueta corta ("512 KB", "1,2 MB"). Exportada para tests. */
export function formatBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
}

/** Ícono según el tipo de archivo (los cupones/facturas son PDF). */
export function iconForMime(mimeType) {
  const mime = String(mimeType || "").toLowerCase();
  if (mime === "application/pdf") return "file-text";
  if (mime.startsWith("image/")) return "paperclip";
  return "paperclip";
}

/**
 * Tarjeta de adjunto con descarga. Los bytes nunca viajan en el mensaje: se
 * piden al server con el token de sesión y se entregan como blob → <a download>,
 * así el token no queda en la URL ni en el historial del navegador.
 */
export function AttachmentCard({ attachment }) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  if (!attachment?.id) return null;

  const sizeLabel = formatBytes(attachment.byteSize);

  const handleDownload = async () => {
    const convId = conversation.value.id;
    if (!convId || downloading) return;
    setDownloading(true);
    setError(null);
    let objectUrl = null;
    try {
      const blob = await downloadChatAttachment(convId, attachment.id);
      objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = attachment.fileName || "adjunto";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      track("attachment_download", { ok: true });
    } catch (err) {
      // 404/410/403 → el archivo ya no está disponible para esta sesión.
      const gone = err?.status === 404 || err?.status === 410 || err?.status === 403;
      setError(gone ? strings.attachmentUnavailable : strings.errorGeneric);
      track("attachment_download", { ok: false, code: err?.code || err?.status || null });
    } finally {
      if (objectUrl) setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
      setDownloading(false);
    }
  };

  return (
    <div class="mt-2">
      <button
        type="button"
        onClick={handleDownload}
        disabled={downloading}
        class="flex w-full items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-left transition hover:bg-slate-100 disabled:opacity-60 dark:border-slate-600 dark:bg-slate-700/60 dark:hover:bg-slate-700"
      >
        <Icon name={iconForMime(attachment.mimeType)} size={20} class="shrink-0 text-flux-primary dark:text-flux-cyan" />
        <span class="min-w-0 flex-1">
          <span class="block truncate text-xs font-medium">{attachment.fileName || "Adjunto"}</span>
          <span class="block text-[11px] opacity-70">
            {downloading ? strings.attachDownloading : [sizeLabel, strings.attachDownload].filter(Boolean).join(" · ")}
          </span>
        </span>
        <Icon
          name={downloading ? "loader-circle" : "download"}
          size={16}
          class={`shrink-0 opacity-70 ${downloading ? "animate-spin" : ""}`}
        />
      </button>
      {error && <span class="mt-1 block text-[11px] font-medium text-rose-600 dark:text-rose-300">{error}</span>}
    </div>
  );
}
